import { useEffect, useState } from "react";
import { api, money, formatApiErrorDetail, qtyLabel, formatStatus } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Eye, Lock, AlertTriangle } from "lucide-react";

const DELIV_ORDER = ["pending", "confirmed", "in_transit", "delivered"];
const BUYER_PAY = ["pending", "invoiced", "paid"];
const SUP_PAY = ["pending", "approved", "paid"];

export default function Transactions() {
  const { user } = useAuth();
  const { can, loaded: permsLoaded } = usePermissions();
  const [txs, setTxs] = useState([]);
  const [openTx, setOpenTx] = useState(null);
  const [confirmSupPaid, setConfirmSupPaid] = useState(null); // { tx, buyerStatus }

  const isEmployee = user.role === "admin" || user.role === "account_manager";
  const isSupplier = user.role === "supplier";

  // Phase 3: deny-by-default until permissions are resolved so employees
  // never see a privileged control briefly during the perms fetch.
  const permReady = !isEmployee || permsLoaded;
  const canWriteDelivery = permReady && can("tx.delivery.write");
  const canWriteBuyerPay = permReady && can("payment.buyer.write");
  const canWriteSupPay = permReady && can("payment.supplier.write");
  const canReadMargin = permReady && can("commercial.margin.read");
  const canReadSupCost = permReady && can("commercial.supplier_cost.read");
  // Show the supplier-total column to suppliers (their own view) or to
  // staff with `commercial.supplier_cost.read`.
  const showSupCostCol = isSupplier || canReadSupCost;
  // "CA revenue" column is server-side redacted for staff without
  // commercial.margin.read; hide the whole column too.
  const showMarginCol = canReadMargin;
  // Buyer-payment column stays visible to buyer + any staff (existing
  // behaviour). Buyer-payment editor is permission-gated.
  const showBuyerPayCol = !isSupplier;
  // Supplier-payment column: supplier's own view or staff.
  const showSupPayCol = isSupplier || isEmployee;

  const load = async () => { const { data } = await api.get("/transactions"); setTxs(data); };
  useEffect(() => { load(); }, []);

  const openDetails = async (tx) => {
    setOpenTx(tx);
    try {
      const { data } = await api.get(`/transactions/${tx.id}`);
      setOpenTx(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load transaction details");
    }
  };

  const adminUpdate = async (id, patch, extra = {}) => {
    try {
      await api.patch(`/transactions/${id}`, { ...patch, ...extra });
      toast.success("Updated");
      await load();
      if (openTx && openTx.id === id) {
        try { const { data } = await api.get(`/transactions/${id}`); setOpenTx(data); } catch {}
      }
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "buyer_not_paid") {
        // Trigger confirmation dialog instead of failing silently.
        const tx = txs.find((t) => t.id === id) || openTx;
        setConfirmSupPaid({ tx, buyerStatus: detail.buyer_payment_status, pendingPatch: patch });
        return false;
      }
      toast.error(formatApiErrorDetail(detail));
      return false;
    }
  };

  const confirmSupplierPaidNow = async () => {
    if (!confirmSupPaid) return;
    const ok = await adminUpdate(confirmSupPaid.tx.id, confirmSupPaid.pendingPatch, { confirm_supplier_paid_before_buyer: true });
    if (ok) setConfirmSupPaid(null);
  };

  const supplierAdvance = async (id, newStatus) => {
    try {
      await api.patch(`/transactions/${id}/delivery-status`, { delivery_status: newStatus });
      toast.success(`Delivery marked ${formatStatus(newStatus)}`);
      await load();
      if (openTx && openTx.id === id) setOpenTx({ ...openTx, delivery_status: newStatus });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const allowedForwardOptions = (current) => {
    const i = DELIV_ORDER.indexOf(current || "pending");
    return DELIV_ORDER.slice(i < 0 ? 0 : i);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Transactions</div>
        <h1 className="font-display text-3xl font-extrabold text-[#0A2540] tracking-tight mt-1">
          {user.role === "buyer" ? "My Orders" : isSupplier ? "Awarded Contracts" : "All Transactions"}
        </h1>
        {isSupplier && (<p className="text-sm text-slate-500 mt-2">Advance the delivery status as your shipment progresses. Payment is recorded by Caprocure.</p>)}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        {txs.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No transactions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Reference</th>
                  <th className="px-6 py-3">RFQ</th>
                  <th className="px-6 py-3">Qty × UOM</th>
                  {!isSupplier && <th className="px-6 py-3">Buyer total</th>}
                  {showSupCostCol && <th className="px-6 py-3">Sup. total</th>}
                  {showMarginCol && <th className="px-6 py-3 text-[#C8102E]">CA revenue</th>}
                  <th className="px-6 py-3">Delivery</th>
                  {showBuyerPayCol && <th className="px-6 py-3">Buyer payment</th>}
                  {showSupPayCol && <th className="px-6 py-3">Supplier payment</th>}
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {txs.map((t) => {
                  const forward = isSupplier ? allowedForwardOptions(t.delivery_status) : [];
                  const supplierDelivLocked = isSupplier && t.delivery_status === "delivered";
                  const buyerPay = t.buyer_payment_status ?? t.payment_status ?? "pending";
                  return (
                    <tr key={t.id} data-testid={`tx-row-${t.id}`}>
                      <td className="px-6 py-4 font-mono-num text-xs">{t.reference}</td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-[#0A2540]">{t.rfq?.title}</div>
                        <div className="font-mono-num text-xs text-slate-500">{t.rfq?.reference}</div>
                      </td>
                      <td className="px-6 py-4" data-testid={`tx-qty-${t.id}`}>{qtyLabel(t.quantity ?? t.rfq?.quantity, t.uom || t.rfq?.uom)}</td>
                      {!isSupplier && <td className="px-6 py-4 font-mono-num font-bold" data-testid={`tx-buyer-total-${t.id}`}>{money(t.buyer_total_price ?? t.buyer_price, t.currency)}</td>}
                      {showSupCostCol && <td className="px-6 py-4 font-mono-num font-bold" data-testid={`tx-sup-total-${t.id}`}>{money(t.supplier_total_cost ?? t.supplier_cost, t.currency)}</td>}
                      {showMarginCol && <td className="px-6 py-4 font-mono-num font-bold text-[#C8102E]" data-testid={`tx-margin-${t.id}`}>{money(t.margin_amount, t.currency)}</td>}
                      <td className="px-6 py-4">
                        {canWriteDelivery ? (
                          <Select value={t.delivery_status} onValueChange={(v) => adminUpdate(t.id, { delivery_status: v })}>
                            <SelectTrigger className="w-36 h-8" data-testid={`tx-delivery-${t.id}`}><SelectValue>{formatStatus(t.delivery_status)}</SelectValue></SelectTrigger>
                            <SelectContent>{DELIV_ORDER.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : isSupplier ? (
                          supplierDelivLocked ? <StatusBadge status={t.delivery_status} /> : (
                            <Select value={t.delivery_status} onValueChange={(v) => supplierAdvance(t.id, v)}>
                              <SelectTrigger className="w-36 h-8" data-testid={`sup-delivery-${t.id}`}><SelectValue>{formatStatus(t.delivery_status)}</SelectValue></SelectTrigger>
                              <SelectContent>{forward.map((s) => <SelectItem key={s} value={s} data-testid={`sup-delivery-opt-${s}`}>{formatStatus(s)}</SelectItem>)}</SelectContent>
                            </Select>
                          )
                        ) : (<StatusBadge status={t.delivery_status} />)}
                      </td>
                      {showBuyerPayCol && (
                        <td className="px-6 py-4">
                          {canWriteBuyerPay ? (
                            <Select value={buyerPay} onValueChange={(v) => adminUpdate(t.id, { buyer_payment_status: v })}>
                              <SelectTrigger className="w-32 h-8" data-testid={`tx-buyer-pay-${t.id}`}><SelectValue>{formatStatus(buyerPay)}</SelectValue></SelectTrigger>
                              <SelectContent>{BUYER_PAY.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (<StatusBadge status={buyerPay} />)}
                        </td>
                      )}
                      {showSupPayCol && (
                        <td className="px-6 py-4">
                          {canWriteSupPay ? (
                            <Select value={t.supplier_payment_status ?? "pending"} onValueChange={(v) => adminUpdate(t.id, { supplier_payment_status: v })}>
                              <SelectTrigger className="w-32 h-8" data-testid={`tx-sup-pay-${t.id}`}><SelectValue>{formatStatus(t.supplier_payment_status)}</SelectValue></SelectTrigger>
                              <SelectContent>{SUP_PAY.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : (<StatusBadge status={t.supplier_payment_status ?? "pending"} />)}
                        </td>
                      )}
                      <td className="px-6 py-4"><StatusBadge status={t.status} /></td>
                      <td className="px-6 py-4 text-right">
                        <Button size="sm" variant="outline" onClick={() => openDetails(t)} data-testid={`tx-view-${t.id}`} className="gap-1">
                          <Eye className="w-3.5 h-3.5" /> View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail modal */}
      <Dialog open={!!openTx} onOpenChange={(o) => !o && setOpenTx(null)}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] sm:max-h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
            <DialogTitle className="font-display text-[#0A2540]">Awarded contract details</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 py-4">
          {openTx && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Fact label="Transaction reference" value={openTx.reference} testid="detail-tx-ref" />
                <Fact label="RFQ" value={<span><span className="font-mono-num text-xs text-slate-500">{openTx.rfq?.reference}</span><br />{openTx.rfq?.title}</span>} testid="detail-rfq" />
                <Fact label="Quantity" value={qtyLabel(openTx.quantity ?? openTx.rfq?.quantity, openTx.uom || openTx.rfq?.uom)} testid="detail-qty" />
                {!isSupplier && <Fact label="Buyer" value={openTx.buyer?.company_name || "—"} />}
                {isEmployee && <Fact label="Supplier" value={openTx.supplier?.company_name || "—"} />}
                <Fact label="Shipping / delivery" value={openTx.shipping_terms || "—"} testid="detail-shipping-terms" />
                <Fact label="Shipping cost" value={money(openTx.shipping_cost ?? 0, openTx.currency)} mono testid="detail-shipping-cost" />
                <Fact label="Lead time" value={openTx.lead_time_days != null ? `${openTx.lead_time_days} days` : "—"} testid="detail-lead" />
                <Fact label="Warranty" value={openTx.warranty || "—"} testid="detail-warranty" />
                <Fact label="Delivery status" value={<StatusBadge status={openTx.delivery_status} />} testid="detail-delivery-status" />
                {isEmployee && openTx.status === "completed" && openTx.completed_at && (
                  <Fact label="Completed on" value={new Date(openTx.completed_at).toLocaleString()} testid="detail-completed-at" />
                )}
              </div>

              {/* Financial / Payment section */}
              <div className="rounded-lg border-2 border-[#0A2540]/15 bg-[#0A2540]/[0.02] p-4" data-testid="detail-financial-section">
                <div className="eyebrow text-[#0A2540] mb-3">Financial · Payment</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {!isSupplier && (
                    <div className="bg-white border border-slate-200 rounded-md p-3">
                      <div className="eyebrow text-slate-500">Buyer amount (receivable)</div>
                      <div className="font-mono-num text-xl font-extrabold text-[#0A2540]" data-testid="detail-buyer-amount">{money(openTx.buyer_total_price ?? openTx.buyer_price, openTx.currency)}</div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="text-xs text-slate-500">Buyer payment status</span>
                        {canWriteBuyerPay ? (
                          <Select value={openTx.buyer_payment_status ?? openTx.payment_status ?? "pending"} onValueChange={(v) => adminUpdate(openTx.id, { buyer_payment_status: v })}>
                            <SelectTrigger className="w-32 h-8" data-testid="detail-buyer-pay-select"><SelectValue>{formatStatus(openTx.buyer_payment_status ?? openTx.payment_status)}</SelectValue></SelectTrigger>
                            <SelectContent>{BUYER_PAY.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (<StatusBadge status={openTx.buyer_payment_status ?? openTx.payment_status} />)}
                      </div>
                    </div>
                  )}

                  {(isSupplier || isEmployee) && (
                    <div className="bg-white border border-slate-200 rounded-md p-3">
                      <div className="eyebrow text-slate-500">Supplier amount (payable)</div>
                      <div className="font-mono-num text-xl font-extrabold text-[#0A2540]" data-testid="detail-supplier-amount">{money(openTx.supplier_total_cost ?? openTx.supplier_cost, openTx.currency)}</div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="text-xs text-slate-500">Supplier payment status</span>
                        {canWriteSupPay ? (
                          <Select value={openTx.supplier_payment_status ?? "pending"} onValueChange={(v) => adminUpdate(openTx.id, { supplier_payment_status: v })}>
                            <SelectTrigger className="w-32 h-8" data-testid="detail-sup-pay-select"><SelectValue>{formatStatus(openTx.supplier_payment_status)}</SelectValue></SelectTrigger>
                            <SelectContent>{SUP_PAY.map((s) => <SelectItem key={s} value={s}>{formatStatus(s)}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (<StatusBadge status={openTx.supplier_payment_status ?? "pending"} />)}
                      </div>
                    </div>
                  )}

                  {canReadMargin && openTx.margin_amount != null && (
                    <div className="bg-[#C8102E]/[0.04] border border-[#C8102E]/20 rounded-md p-3 md:col-span-2">
                      <div className="eyebrow text-[#C8102E]">Caprocure revenue</div>
                      <div className="font-mono-num text-xl font-extrabold text-[#C8102E]" data-testid="detail-margin">{money(openTx.margin_amount, openTx.currency)}</div>
                      <div className="text-xs text-slate-500 mt-1">Buyer receivable minus supplier payable.</div>
                    </div>
                  )}
                </div>
              </div>

              {isSupplier && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 flex items-start gap-2">
                  <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
                  <span>Your quotation values (unit price, shipping, other costs, warranty and terms) are locked at award. Payment is issued by Caprocure.</span>
                </div>
              )}

              {isSupplier && openTx.delivery_status !== "delivered" && (
                <div className="pt-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Advance delivery status</div>
                  <div className="flex flex-wrap gap-2">
                    {allowedForwardOptions(openTx.delivery_status).filter((s) => s !== openTx.delivery_status).map((s) => (
                      <Button key={s} size="sm" className="bg-[#0A2540] hover:bg-[#061A2E]" onClick={() => supplierAdvance(openTx.id, s)} data-testid={`detail-advance-${s}`}>
                        Mark {formatStatus(s)}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm supplier=paid while buyer not paid */}
      <Dialog open={!!confirmSupPaid} onOpenChange={(o) => !o && setConfirmSupPaid(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-sup-paid-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0A2540]">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Confirm supplier payment
            </DialogTitle>
          </DialogHeader>
          {confirmSupPaid && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-700">
                Buyer funds for <span className="font-mono-num font-semibold">{confirmSupPaid.tx?.reference}</span> have not yet been recorded as received
                (current buyer payment status: <span className="font-semibold">{formatStatus(confirmSupPaid.buyerStatus)}</span>).
              </p>
              <p className="text-slate-700">
                Marking the supplier as <span className="font-semibold">Paid</span> now means Caprocure is disbursing
                <span className="font-mono-num font-semibold"> {money(confirmSupPaid.tx?.supplier_total_cost, confirmSupPaid.tx?.currency)}</span> to the supplier ahead of buyer collection.
              </p>
              <p className="text-xs text-slate-500">Only the supplier payment status will change. Buyer payment status is unaffected.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSupPaid(null)} data-testid="confirm-cancel">Cancel</Button>
            <Button className="bg-[#0A2540] hover:bg-[#061A2E]" onClick={confirmSupplierPaidNow} data-testid="confirm-supplier-paid">Yes, mark supplier paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Fact({ label, value, mono, bold, accent, testid }) {
  const cls = [
    "font-medium",
    mono ? "font-mono-num" : "",
    bold ? "font-bold" : "",
    accent === "maple" ? "text-[#C8102E]" : "text-[#0A2540]",
  ].filter(Boolean).join(" ");
  return (
    <div className="bg-slate-50 p-3 rounded-md">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 ${cls}`} data-testid={testid}>{value ?? "—"}</div>
    </div>
  );
}
